import type { PoaFeeCandidate } from "../api";
import { fmtDate } from "../utils";

/** Etykiety statusów opłaty 17 zł za pełnomocnictwo (wspólne dla wniosków i
 *  zamówień wypisów / MZ / MPZP). */
export const POA_FEE_STATUS_LABELS: Record<string, string> = {
  pending_approval: "czeka na akceptację",
  approved: "zaakceptowana — do opłacenia",
  payment_uploaded: "OPŁACONA",
  acknowledged: "OPŁACONA (potwierdzona)",
  rejected: "odrzucona",
  withdrawn: "wycofana",
};

/** Jedna linia selecta: urząd · kwota · tytuł · data opłacenia ALBO „NIEOPŁACONA". */
export function poaFeeOptionLabel(c: PoaFeeCandidate, suggestedFeeId?: string): string {
  const paidInfo = c.paid
    ? `opłacona ${c.paid_at ? fmtDate(c.paid_at) : "(brak daty)"}`
      + (c.confirmations.length > 1 ? ` (${c.confirmations.length} potwierdzenia)` : "")
    : `NIEOPŁACONA (${POA_FEE_STATUS_LABELS[c.status] ?? c.status}) — można podpiąć`;
  return `${c.id === suggestedFeeId ? "✓ sugerowana · " : ""}${c.office_name} · ${c.amount} zł · ${c.transfer_name} · ${paidInfo}`;
}

/** Opcje selecta „podepnij istniejącą opłatę”: opłacone (z datą opłacenia) osobno
 *  od czekających dopiero na opłacenie — te drugie też da się podpiąć. */
export function PoaFeeOptionGroups({
  options, suggestedFeeId,
}: {
  options: PoaFeeCandidate[];
  suggestedFeeId?: string;
}) {
  const paid = options.filter((c) => c.paid);
  const unpaid = options.filter((c) => !c.paid);
  return (
    <>
      <option value="">— wybierz —</option>
      {paid.length > 0 && (
        <optgroup label="Opłacone — z datą opłacenia">
          {paid.map((c) => (
            <option key={c.id} value={c.id}>{poaFeeOptionLabel(c, suggestedFeeId)}</option>
          ))}
        </optgroup>
      )}
      {unpaid.length > 0 && (
        <optgroup label="Jeszcze nieopłacone — można podpiąć">
          {unpaid.map((c) => (
            <option key={c.id} value={c.id}>{poaFeeOptionLabel(c, suggestedFeeId)}</option>
          ))}
        </optgroup>
      )}
    </>
  );
}

/** Ostrzeżenie pod selectem, gdy wybrana opłata nie ma jeszcze potwierdzenia przelewu.
 *  `consequence` mówi, co to znaczy w danym module (wniosek czeka / zamówienie pokaże
 *  datę po opłaceniu). */
export function UnpaidPoaFeeNote({ fee, consequence }: { fee?: PoaFeeCandidate; consequence: string }) {
  if (!fee || fee.paid) return null;
  return (
    <div className="mt-1 text-amber-700 dark:text-amber-400">
      Ta opłata nie jest jeszcze opłacona ({POA_FEE_STATUS_LABELS[fee.status] ?? fee.status}). {consequence}
    </div>
  );
}
